import { validateFilePackage } from "fond/fileValidation";
import { MuPowerInputLayerId, MuPowerInputLayerIds } from "fond/types";
import { ExpectedFileMatch, fuzzyMatchFile } from "fond/utils/fuzzyFileMatch";

const SUPPORTED_EXTENSIONS = [".shp", ".dbf", ".prj", ".shx", ".cpg", ".geojson", ".json", ".kml"];

export const MU_POWER_BULK_MATCH_CONFIG: ExpectedFileMatch<MuPowerInputLayerId>[] = [
  {
    id: MuPowerInputLayerIds.boundary,
    baseName: "boundary",
    keywords: ["boundary", "bounds"],
    allowedExtensions: SUPPORTED_EXTENSIONS,
  },
  {
    id: MuPowerInputLayerIds.addresses,
    baseName: "addresses",
    keywords: ["address", "addresses"],
    allowedExtensions: SUPPORTED_EXTENSIONS,
  },
  {
    id: MuPowerInputLayerIds.parcels,
    baseName: "parcels",
    keywords: ["parcel", "parcels"],
    allowedExtensions: SUPPORTED_EXTENSIONS,
  },
  {
    id: MuPowerInputLayerIds.streets,
    baseName: "streets",
    keywords: ["street", "streets", "roads", "road", "ug", "ug_path"],
    allowedExtensions: SUPPORTED_EXTENSIONS,
  },
  {
    id: MuPowerInputLayerIds.poles,
    baseName: "poles",
    keywords: ["pole", "poles", "aer_points", "aer"],
    allowedExtensions: SUPPORTED_EXTENSIONS,
  },
];

const getBaseName = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  return (lastDot === -1 ? fileName : fileName.slice(0, lastDot)).toLowerCase();
};

export const groupFilesByBaseName = (files: File[]): Map<string, File[]> => {
  const groups = new Map<string, File[]>();
  for (const file of files) {
    const base = getBaseName(file.name);
    const group = groups.get(base);
    if (group) {
      group.push(file);
    } else {
      groups.set(base, [file]);
    }
  }
  return groups;
};

export interface BulkUploadGroup {
  baseName: string;
  files: File[];
  matchedLayerId: MuPowerInputLayerId | null;
  score: number;
  validationError: string | null;
}

export const matchDroppedFiles = (files: File[]): BulkUploadGroup[] => {
  const grouped = groupFilesByBaseName(files);
  const taken = new Set<MuPowerInputLayerId>();
  const unresolved: Array<{ baseName: string; files: File[]; validationError: string | null }> = [];
  const resolved: BulkUploadGroup[] = [];

  for (const [baseName, groupFiles] of grouped) {
    const { isPackageValid, error, expectedExtensions } = validateFilePackage(groupFiles);
    let validationError: string | null = null;
    if (!isPackageValid) {
      if (error) {
        validationError = error.message;
      } else if (expectedExtensions && expectedExtensions.length > 0) {
        const received = new Set(groupFiles.map((f) => f.name.slice(f.name.lastIndexOf(".") + 1).toLowerCase()));
        const missing = expectedExtensions.filter((ext) => !received.has(ext.toLowerCase()));
        validationError = `Incomplete file set. Missing: ${missing.join(", ")}`;
      } else {
        validationError = "Unsupported file package";
      }
    }

    const representative = groupFiles[0];
    const match = fuzzyMatchFile(representative.name, MU_POWER_BULK_MATCH_CONFIG);

    if (match && !taken.has(match.id)) {
      taken.add(match.id);
      resolved.push({
        baseName,
        files: groupFiles,
        matchedLayerId: match.id,
        score: match.score,
        validationError,
      });
    } else {
      unresolved.push({ baseName, files: groupFiles, validationError });
    }
  }

  for (const group of unresolved) {
    resolved.push({
      baseName: group.baseName,
      files: group.files,
      matchedLayerId: null,
      score: 0,
      validationError: group.validationError,
    });
  }

  return resolved;
};
